import type { MenuProps } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { MenuInfo } from 'rc-menu/lib/interface'
import { startTransition, Suspense, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'
import { CloseOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined } from '@ant-design/icons'

import { setLocalStorageData, STORAGE_KEY } from 'services/localStorage.service'
import { useStore } from 'store/useStore'
import { PromoPageKeys } from 'sections/PromoSection/pages/PromoPage/types'
import { MainLogo } from 'components/MainLogo/MainLogo'
import { LangSwitcher, Margin } from 'components/ui'
import { Href } from 'components/ui/Href/Href'
import { Button, Drawer, Dropdown, Flex, Header, Menu, Text } from 'components/uiKit'
import { checkIfPWAInstalled } from 'utils/helpers/checkIfPWAInstalled'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { isLanguage } from 'utils/helpers/i18n/getCurrentLanguage'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { useResize } from 'utils/hooks/useResize'
import { AuthRoutesEnum, LKRoutesEnum, PromoGeRoutesEnum, PromoRoutesEnum } from 'shared/constants/routes'
import { URLS } from 'shared/constants/urls'
import { colors, SEARCH_PARAMS } from 'shared/constants/values'
import { SupportedLanguages } from 'shared/types/types'

import { OfferModal } from '../modules/UserModule/components/OfferModal/OfferModal'
import { OWNER_STATUS } from '../modules/UserModule/types'
import { menuItems, MenuKeys } from './data'
import { LKGeneralLayoutKeys } from './types'

import CaretIcon from 'assets/icons/caret.svg?react'
import LogoIcon from 'assets/icons/logo/logo-new-white.svg?react'
import LogoIconGe from 'assets/icons/logo/logo-new-white-ge.svg?react'
import s from './LKGeneralLayout.module.scss'

export const LKGeneralLayout = observer(() => {
  const { appStore, viewOrderUiStore, loginUiStore } = useStore()

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [personalMenuCollapsed, setPersonalMenuCollapsed] = useState(false)
  const [pageKey, setPageKey] = useState(location.pathname)
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { width, isMobile } = useResize()

  const pickPointType = searchParams.get(SEARCH_PARAMS.pickPointType)
  const latitude = searchParams.get(SEARCH_PARAMS.latitude)
  const longitude = searchParams.get(SEARCH_PARAMS.longitude)
  const subsidyHexType = searchParams.get(SEARCH_PARAMS.subsidyHexType)
  const mobileHash = searchParams.get(SEARCH_PARAMS.mobileHash)

  const getKey = (key: string) => `${LKGeneralLayoutKeys.LKGeneralLayout}.${key}`
  const getPromoKey = (key: string) => `${PromoPageKeys.shared}.${key}`

  const isPWAInstalled = checkIfPWAInstalled()
  const menuItemsFiltered = menuItems.filter(
    (item) =>
      ((item.key !== MenuKeys.pwa && isPWAInstalled) || !isPWAInstalled) &&
      !(isGeDomain() && item.key === MenuKeys.pvz),
  )

  const handleProfile = async () => {
    setPersonalMenuCollapsed(false)
    setPageKey(LKRoutesEnum.profile)
    navigate(LKRoutesEnum.profile)
  }

  const exitButtonHandler = async () => {
    await appStore.logout()
    navigate(isGeDomain() ? PromoGeRoutesEnum.login : AuthRoutesEnum.login, { replace: true })
    location.reload()
  }

  const dropdownItems: MenuProps['items'] = [
    {
      key: LKRoutesEnum.profile,
      label: (
        <Flex className={s.personalMenuItem} align="center" onClick={handleProfile}>
          <Text>{t(getKey('Профиль'))}</Text>
        </Flex>
      ),
      icon: <UserOutlined />,
    },
    {
      key: 'logout',
      label: (
        <Flex className={s.personalMenuItem} align="center" onClick={exitButtonHandler}>
          <Text>{t(getKey('Выйти'))}</Text>
        </Flex>
      ),
      icon: <LogoutOutlined />,
    },
  ]

  useAsyncEffect(async () => {
    appStore.setLoadingApp(true)

    if (mobileHash) {
      searchParams.delete(SEARCH_PARAMS.mobileHash)
      setSearchParams(searchParams)
      await loginUiStore.useAuthHash(mobileHash)
    }

    await appStore.bootstrap()

    if (latitude && longitude) {
      setLocalStorageData(STORAGE_KEY.COORDINATES, {
        latitude,
        longitude,
      })
    }

    if (pickPointType) {
      setLocalStorageData(STORAGE_KEY.PICK_POINT_TYPE, pickPointType)
    }

    if (subsidyHexType) {
      setLocalStorageData(STORAGE_KEY.SUBSIDY_HEX_TYPE, subsidyHexType)
    }

    if (!appStore.isLoadingUserInfo && !appStore.user.authenticated) {
      navigate(AuthRoutesEnum.login, { replace: true })
    }

    if (appStore.needRegistration) {
      navigate(AuthRoutesEnum.registration, { replace: true })
    }

    setTimeout(() => {
      appStore.setLoadingApp(false)
    }, 500)
  }, [])

  useEffect(() => {
    if (!appStore.isLoadingApp) {
      if (!appStore.isLoadingUserInfo && !appStore.user.authenticated) {
        navigate(AuthRoutesEnum.login, { replace: true })
      }
      if (appStore.user.info.owner === OWNER_STATUS.ROLE_NONE) {
        navigate(AuthRoutesEnum.registration, { replace: true })
      }
    }
  }, [appStore.isLoadingUserInfo, appStore.user.authenticated, appStore.isLoadingApp, appStore.user.info.owner])

  useEffect(() => {
    if (width < 1100) {
      setSidebarCollapsed(true)
    }
  }, [width])

  useEffect(() => {
    const routeList = [MenuKeys.orders, MenuKeys.pvz, MenuKeys.tickets]
    const matchedKey = routeList.find((route) => location.pathname.includes(route))

    if (matchedKey) {
      setPageKey(matchedKey)
    }
  }, [location.pathname])

  const handleLeftMenuItem = useCallback((info: MenuInfo) => {
    handleMenuItemClick(info.key as MenuKeys)
  }, [])

  const handleDrawerItem = useCallback((key: MenuKeys) => {
    setDrawerOpen(false)
    handleMenuItemClick(key)
  }, [])

  const handleMenuItemClick = useCallback(
    (key: MenuKeys) => {
      startTransition(() => {
        if (key === MenuKeys.pwa) {
          viewOrderUiStore.pwaEvent?.prompt()
        } else {
          setPageKey(key)
          navigate(key)
        }
      })
    },
    [viewOrderUiStore],
  )

  const handleBurgerButton = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed)
    setDrawerOpen(!isDrawerOpen)
  }, [sidebarCollapsed, isDrawerOpen])

  const privatePolicyLink = () => {
    return (
      <Margin mb={20}>
        <Flex justify="center">
          <Href
            className={s.policyLink}
            href={isGeDomain() ? URLS.wbMarketPrivatePolicyGe : URLS.wbMarketPrivatePolicy}
            linkType="pathname"
            target="_blank"
          >
            {t(getPromoKey('Политика конфиденциальности'))}
          </Href>
        </Flex>
      </Margin>
    )
  }

  return appStore.isLoadingApp ? (
    <Flex className={s.mainLogoConainer}>
      <MainLogo />
    </Flex>
  ) : (
    <div className={s.LKGeneralLayout}>
      {!isMobile && (
        <Button onClick={handleBurgerButton} className={s.burgerButton}>
          {sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </Button>
      )}
      <Header className={s.header} style={{ padding: isMobile ? '0px 20px' : '0px 50px' }}>
        <Flex align="center" style={{ marginLeft: isMobile ? 0 : 50 }}>
          <Href href={PromoRoutesEnum.root} type="white">
            {isLanguage(SupportedLanguages.GE) ? (
              <LogoIconGe className={`${s.logo} , ${s.logoGe}`} />
            ) : isMobile ? (
              <LogoIcon className={s.logoWithoutText} />
            ) : (
              <LogoIcon className={s.logo} />
            )}
          </Href>
        </Flex>

        <Flex align="center" gap={8}>
          {isGeDomain() && <LangSwitcher style={{ height: 32 }} />}

          <Dropdown menu={{ items: dropdownItems }} onOpenChange={(isOpen) => setPersonalMenuCollapsed(isOpen)}>
            <Flex className={s.personalMenuWrapper}>
              <Flex gap={16} align="center">
                {isMobile ? (
                  <UserOutlined className={s.userIcon} />
                ) : (
                  <>
                    <Text className={s.name} style={{ color: 'white' }}>
                      {appStore.user.info.first_name} {appStore.user.info.last_name}
                    </Text>
                    <CaretIcon
                      className={s.caret}
                      style={{ transform: personalMenuCollapsed ? 'rotate(180deg)' : undefined }}
                    />
                  </>
                )}
              </Flex>
            </Flex>
          </Dropdown>

          {isMobile && (
            <Button onClick={handleBurgerButton}>
              {(!isMobile && sidebarCollapsed) || (isMobile && isDrawerOpen) ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )}
            </Button>
          )}
        </Flex>
      </Header>

      <Flex>
        {!isMobile && (
          <Flex vertical style={{ backgroundColor: colors.darkAnt }}>
            <Menu
              theme="dark"
              mode="inline"
              inlineCollapsed={sidebarCollapsed}
              onClick={handleLeftMenuItem}
              selectedKeys={[pageKey]}
              style={{
                width: sidebarCollapsed ? 65 : 250,
                flex: 1,
              }}
              items={menuItemsFiltered}
            />

            {!sidebarCollapsed && privatePolicyLink()}
          </Flex>
        )}
        {isMobile && (
          <Drawer
            open={isDrawerOpen}
            onClose={() => setDrawerOpen(false)}
            placement="right"
            style={{ backgroundColor: colors.darkAnt }}
            footer={privatePolicyLink()}
            width={'70%'}
            closeIcon={<CloseOutlined style={{ color: colors.white }} />}
          >
            <Flex vertical gap={10}>
              {menuItemsFiltered.map((menuItem) => (
                <Flex
                  className={classNames(s.drawerItem, { [s.drawerItemActive]: pageKey === menuItem.key })}
                  key={menuItem.key}
                  align="center"
                  onClick={() => handleDrawerItem(menuItem.key)}
                >
                  <Flex className={s.drawerItemIconContainer} align="center" justify="center">
                    {menuItem.icon}
                  </Flex>
                  <Text className={s.drawerItemText}>{menuItem.label}</Text>
                </Flex>
              ))}
            </Flex>
          </Drawer>
        )}

        <Flex className={s.content}>
          <Suspense fallback={<MainLogo />}>
            <Outlet />
          </Suspense>
        </Flex>
      </Flex>
      <OfferModal />
    </div>
  )
})
